// FinancialDataSection.jsx
import React, { useMemo, useState } from "react";
import {
  Info, CheckCircle2, AlertCircle,
  FileText, CalendarRange, CalendarDays, ClipboardList,
  ShieldAlert, // ShieldCheck
  CircleDollarSign, Banknote, Coins, Wallet, Boxes, Layers,
  Landmark, TrendingUp, BarChart3, FileBarChart, ChevronDown, ChevronUp
} from "lucide-react";

/** --- Visual primitives --- */
const Karta = ({ children, style }) => (
  <div
    style={{
      border: "1px solid #e6e9ee",
      borderRadius: 16,
      padding: 16,
      background: "#fff",
      boxShadow: "0 16px 48px rgba(16,24,40,0.16), 0 8px 22px rgba(16,24,40,0.12)",
      ...style,
    }}
  >
    {children}
  </div>
);

const Sekcja = ({ title, icon: Icon = Info, children }) => (
  <Karta>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
      {Icon ? <Icon size={18} color="#0b74e5" /> : null}
      <h3 style={{ margin: 0, fontSize: 18, color: "#0b74e5", textDecorationThickness: "2px", textUnderlineOffset: 4 }}>
        {title}
      </h3>
    </div>
    {children}
  </Karta>
);

const StatusCard = ({ title, status, tone = "info", subtitle, icon: LeadIcon }) => {
  const tones = {
    ok:   { top: "#22c55e", ring: "#22c55e", text: "#111827", sub: "#6b7280", border: "#e6f6ec", Fallback: CheckCircle2 },
    bad:  { top: "#ef4444", ring: "#ef4444", text: "#111827", sub: "#6b7280", border: "#fdecec", Fallback: AlertCircle  },
    warn: { top: "#f59e0b", ring: "#f59e0b", text: "#111827", sub: "#6b7280", border: "#fef3c7", Fallback: AlertCircle  },
    info: { top: "#3b82f6", ring: "#3b82f6", text: "#111827", sub: "#6b7280", border: "#dbeafe", Fallback: Info        },
  };
  const c = tones[tone] || tones.info;
  const IconComp = LeadIcon || c.Fallback;

  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 14,
        background: "#fff",
        boxShadow: "0 6px 24px rgba(16,24,40,0.06)",
        overflow: "hidden",
        display: "grid",
      }}
    >
      <div style={{ height: 6, background: c.top }} />
      <div style={{ display: "grid", gridTemplateColumns: "56px 1fr", gap: 12, padding: 14 }}>
        <div
          style={{
            display: "grid",
            placeItems: "center",
            height: 56,
            width: 56,
            borderRadius: "999px",
            border: `2px solid ${c.ring}`,
            color: c.ring,
          }}
        >
          <IconComp size={28} />
        </div>
        <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
          <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "#6b7280", fontWeight: 700 }}>
            {title}
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: c.text, lineHeight: 1.1 }}>{status}</div>
          {subtitle ? (
            <div style={{ fontSize: 12, color: c.sub, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

/** A customized card specifically for "financial_reports" with badges + collapse */
const FinancialReportsCard = ({ title, countText, listText }) => {
  const periods = useMemo(() =>
    String(listText || "")
      .split(",")
      .map(s => s.trim())
      .filter(Boolean),
  [listText]);

  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? periods : periods.slice(0, 8);

  return (
    <div
      style={{
        border: "1px solid #dbeafe",
        borderRadius: 14,
        background: "#fff",
        boxShadow: "0 6px 24px rgba(16,24,40,0.06)",
        overflow: "hidden",
        display: "grid",
      }}
    >
      <div style={{ height: 6, background: "#3b82f6" }} />
      <div style={{ display: "grid", gridTemplateColumns: "56px 1fr", gap: 12, padding: 14 }}>
        <div
          style={{
            display: "grid",
            placeItems: "center",
            height: 56,
            width: 56,
            borderRadius: "999px",
            border: "2px solid #3b82f6",
            color: "#3b82f6",
          }}
        >
          <FileText size={28} />
        </div>

        <div style={{ display: "grid", gap: 8, minWidth: 0 }}>
          <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "#6b7280", fontWeight: 700 }}>
            {title}
          </div>

          <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1.1 }}>
            {countText || "—"}
          </div>

          {periods.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                Okresy
              </div>

              <div className="badgesWrap">
                {visible.map((p, i) => (
                  <span key={`${p}-${i}`} className="badgePill" title={p}>
                    {p}
                  </span>
                ))}
                {!expanded && periods.length > 8 ? (
                  <div className="fadeRight" />
                ) : null}
              </div>

              {periods.length > 8 && (
                <button
                  onClick={() => setExpanded(v => !v)}
                  className="toggleBtn"
                  type="button"
                >
                  {expanded ? (
                    <>
                      <ChevronUp size={14} />
                      Pokaż mniej
                    </>
                  ) : (
                    <>
                      <ChevronDown size={14} />
                      Pokaż więcej
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* local styles for the badges */}
      <style>{`
        .badgesWrap {
          position: relative;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          max-height: none;
        }
        .badgePill {
          display: inline-flex;
          align-items: center;
          border: 1px solid #e6e9ee;
          background: #f7faff;
          color: #1f2937;
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 12px;
          line-height: 1;
          white-space: nowrap;
        }
        .fadeRight {
          position: absolute;
          right: 0; top: 0; bottom: 0;
          width: 40px;
          background: linear-gradient(90deg, rgba(255,255,255,0), #fff);
          pointer-events: none;
          border-radius: 0 12px 12px 0;
        }
        .toggleBtn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 1px solid #dbeafe;
          background: #eff6ff;
          color: #1e3a8a;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          width: fit-content;
        }
        .toggleBtn:hover { background: #e0efff; }
      `}</style>
    </div>
  );
};

/** --- Helpers --- */
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

const pln = (v) => {
  if (v == null || v === "" || v === "-") return "—";
  const n = Number(String(v).replace(/\s/g, "").replace(",", "."));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 2 });
};

const fmtDate = (val) => {
  if (!val) return "—";
  const s = String(val);
  const parts = s.split(/\s+–\s+|\s+-\s+/);
  const fmtOne = (p) => {
    const d = new Date(p);
    return isNaN(d) ? p : d.toLocaleDateString("pl-PL");
  };
  if (parts.length === 2) return `${fmtOne(parts[0])} – ${fmtOne(parts[1])}`;
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleDateString("pl-PL");
};

const countYearsFromList = (val) => {
  const arr = String(val ?? "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
  return arr.length;
};

function toneFromProbability(text) {
  const v = String(text ?? "").toLowerCase();
  if (v.includes("bardzo nisk")) return "ok";
  if (v.includes("nisk")) return "info";
  if (v.includes("średn") || v.includes("sredn") || v.includes("umiark")) return "warn";
  if (v.includes("wysok")) return "bad";
  return "info";
}

/** Icon maps */
const summaryIconMap = {
  report_obligation: ClipboardList,
  latest_report: CalendarRange,
  latest_available_report: CalendarDays,
  financial_reports: FileText,
  bankruptcy_probability: ShieldAlert,
};

const kpiIconMap = {
  aktywa: Coins,
  pasywa: Layers,
  zapasy: Boxes,
  rozliczenia_miedzyokresowe: FileBarChart,
  srodki_pieniezne: Wallet,
  krotkoterminowe: CircleDollarSign,
  dlugoterminowe: Banknote,
  kapital_wlasny: Landmark,
  przychody_ze_sprzedazy: TrendingUp,
  zysk_strata: BarChart3,
  zysk_strata_poprzedni_rok: BarChart3,
};

/** --- Component --- */
export default function FinancialDataSection({ data = {} }) {
  const g = (k) => data?.[k]?.value ?? null;
  const l = (k) => data?.[k]?.label_short ?? k;
  const long = (k) => data?.[k]?.label_long ?? "";

  // Summary data
  const hasObligation   = g("report_obligation");
  const latestReport    = g("latest_report");
  const latestAvailable = g("latest_available_report");
  const reportsYears    = g("financial_reports");
  const bankruptProb    = g("bankruptcy_probability");

  const summary = [
    {
      key: "report_obligation",
      title: l("report_obligation"),
      status: cap(hasObligation) || "—",
      tone:
        String(hasObligation ?? "").toLowerCase() === "tak"
          ? "ok"
          : String(hasObligation ?? "").toLowerCase() === "nie"
          ? "bad"
          : "info",
      subtitle: long("report_obligation"),
      icon: summaryIconMap.report_obligation,
    },
    {
      key: "latest_report",
      title: l("latest_report"),
      status: latestReport ? fmtDate(latestReport) : "—",
      tone: "info",
      subtitle: long("latest_report"),
      icon: summaryIconMap.latest_report,
    },
    {
      key: "latest_available_report",
      title: l("latest_available_report"),
      status: latestAvailable ? fmtDate(latestAvailable) : "—",
      tone: "info",
      subtitle: long("latest_available_report"),
      icon: summaryIconMap.latest_available_report,
    },
    {
      key: "bankruptcy_probability",
      title: l("bankruptcy_probability"),
      status: bankruptProb ? cap(bankruptProb) : "—",
      tone: toneFromProbability(bankruptProb),
      subtitle: long("bankruptcy_probability"),
      icon: summaryIconMap.bankruptcy_probability,
    },
  ].filter(Boolean);

  // KPI tiles
  const metricsMap = [
    "aktywa",
    "pasywa",
    "zapasy",
    "rozliczenia_miedzyokresowe",
    "srodki_pieniezne",
    "krotkoterminowe",
    "dlugoterminowe",
    "kapital_wlasny",
    "przychody_ze_sprzedazy",
    "zysk_strata",
    "zysk_strata_poprzedni_rok",
  ];

  const metrics = metricsMap
    .map((key) => ({
      key,
      title: l(key),
      subtitle: long(key),
      value: pln(g(key)),
      Icon: kpiIconMap[key] || CircleDollarSign,
    }))
    .filter((m) => m.title);

  const reportsCountText = reportsYears
    ? `${countYearsFromList(reportsYears)} lata/lat`
    : "—";

  if (summary.length === 0 && metrics.length === 0 && !reportsYears) {
    return (
      <Sekcja title="Finanse" icon={BarChart3}>
        <em>Brak danych.</em>
      </Sekcja>
    );
  }

  return (
    <Sekcja title="Finanse" icon={BarChart3}>
      <style>{`
        .summaryGrid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
          margin-bottom: 6px;
        }
        .metricsGrid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
        }
        @media (max-width: 1280px) {
          .summaryGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .metricsGrid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 1024px) {
          .summaryGrid { grid-template-columns: 1fr; }
          .metricsGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 640px) {
          .metricsGrid { grid-template-columns: 1fr; }
        }
        .subTitleSep {
          margin: 10px 0 8px;
          font-size: 12px;
          color: #6b7280;
          letter-spacing: .5px;
          text-transform: uppercase;
          font-weight: 700;
          border-bottom: 1px solid #eef1f5;
          padding-bottom: 6px;
        }
        .kpiTile {
          border: 1px solid #eef1f5;
          border-radius: 12px;
          padding: 12px;
          background: #fafbff;
          display: grid;
          gap: 8px;
          grid-template-columns: 28px 1fr;
          align-items: start;
        }
        .kpiIcon {
          display: grid;
          place-items: center;
          height: 28px;
          width: 28px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          color: #0b74e5;
          background: #f0f6ff;
        }
        .kpiContent { display: grid; gap: 2px; }
        .kpiLabel  { font-size: 12px; color: #6b7280; font-weight: 600; }
        .kpiValue  { font-size: 18px; font-weight: 800; color: #111827; letter-spacing: 0.2px; }
        .kpiSub    { font-size: 11px; color: #9ca3af; }
      `}</style>

      {/* SUMMARY */}
      {(summary.length > 0 || reportsYears) && (
        <>
          <div className="subTitleSep">Podsumowanie sprawozdań</div>
          <div className="summaryGrid">
            {/* Inject the custom FinancialReportsCard first if we have list */}
            {reportsYears && (
              <FinancialReportsCard
                title={l("financial_reports")}
                countText={reportsCountText}
                listText={reportsYears}
              />
            )}

            {summary.map((s) => (
              <StatusCard
                key={s.key}
                title={s.title}
                status={s.status}
                tone={s.tone}
                subtitle={s.subtitle}
                icon={s.icon}
              />
            ))}
          </div>
        </>
      )}

      {/* KPIs */}
      {metrics.length > 0 && (
        <>
          <div className="subTitleSep">Kluczowe wielkości finansowe (ostatnie sprawozdanie)</div>
          <div className="metricsGrid">
            {metrics.map((m) => (
              <div key={m.key} className="kpiTile">
                <div className="kpiIcon">{m.Icon ? <m.Icon size={16} /> : <CircleDollarSign size={16} />}</div>
                <div className="kpiContent">
                  <div className="kpiLabel">{m.title}</div>
                  <div className="kpiValue">{m.value}</div>
                  {m.subtitle ? <div className="kpiSub">{m.subtitle}</div> : null}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Sekcja>
  );
}
